/**
 * useTenantNavigate — react-router's useNavigate with tenant context preserved
 *
 * Returns a navigate function that copies tenant-handle and club-id onto the
 * target URL. The programmatic twin of `TenantLink`.
 *
 * @module lib/navigation/useTenantNavigate
 */

import { useMemo } from 'react';
import {
  useNavigate as useRouterNavigate,
  useSearchParams,
  type NavigateOptions,
} from 'react-router';
import { buildUrlWithCurrentParams } from './utils';

/**
 * Navigate options with automatic param preservation
 */
export interface TenantNavigateOptions extends NavigateOptions {
  preserveParams?: boolean;
}

/**
 * Overloaded so the delta form cannot be handed options it would silently drop —
 * `navigate(-1, { replace: true })` is a type error rather than a no-op. Mirrors
 * react-router's own NavigateFunction, which is overloaded the same way.
 */
export interface TenantNavigateFunction {
  (delta: number): void;
  (to: string, options?: TenantNavigateOptions): void;
}

/**
 * Navigate with tenant-handle and club-id carried onto the target URL
 *
 * The returned function is stable until the URL's params change, like
 * react-router's own `navigate`, so it is safe in an effect's dependencies.
 *
 * @returns Navigate function with automatic param preservation
 *
 * @example
 * ```tsx
 * const navigate = useTenantNavigate();
 *
 * // Automatically preserves tenant-handle and club-id
 * navigate('/booking/confirmed/185');
 *
 * // Opt out of param preservation
 * navigate('/home', { preserveParams: false });
 *
 * // Use with NavigateOptions
 * navigate('/booking', { replace: true, preserveParams: true });
 *
 * // History delta (back/forward) — passed straight through
 * navigate(-1);
 * ```
 */
export function useTenantNavigate(): TenantNavigateFunction {
  const navigate = useRouterNavigate();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    function navigateWithParams(delta: number): void;
    function navigateWithParams(to: string, options?: TenantNavigateOptions): void;
    function navigateWithParams(to: string | number, options?: TenantNavigateOptions): void {
      // History delta (`navigate(-1)` = back). There is no URL to build: a delta replays a
      // history entry that already carries whatever params it was pushed with, so preservation
      // is a no-op rather than a special case. Supporting it is what lets every page in the app
      // use this hook — without it, the "Go back" buttons could never leave React Router's raw
      // useNavigate, and the lint rule steering people here would be unfollowable for them.
      if (typeof to === 'number') {
        navigate(to);
        return;
      }

      const { preserveParams = true, ...navigateOptions } = options || {};

      // Build URL with preserved params if enabled
      const finalUrl = preserveParams
        ? buildUrlWithCurrentParams(to, searchParams)
        : to;

      navigate(finalUrl, navigateOptions);
    }

    return navigateWithParams;
  }, [navigate, searchParams]);
}
